import React, { useContext, useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client";
import { GET_SEASON, SEASONS_WITH_PERMISSION } from "../../GraphQL/Season.js";
import {
  GET_MATCHES_BY_SEASON_ID,
  MATCHES_WITH_PERMISSION,
} from "../../GraphQL/Matches.js";
import { GET_TEAMS_BY_SEASON_ID } from "../../GraphQL/Teams.js";
import { GET_MANAGERS } from "../../GraphQL/Managers";
import { GET_TEAMS_NOT_BY_SEASON_ID } from "../../GraphQL/Teams.js";
import SeasonOverview from "../../Components/Season/SeasonOverview.js";
import LoadingSpinner from "../../Components/UI/LoadingSpinner.js";
import PageError from "../../Components/UI/PageError.js";
import PageLayout from "../../Components/UI/PageLayout.js";
import { LoginContext } from "../../Components/Login/LoginContext";
import TeamTable from "../../Components/Team/TeamTable.js";
import AddTeamToSeasonButton from "../../Components/Team/AddTeamToSeasonButton.js";
import RemoveTeamFromSeasonButton from "../../Components/Team/RemoveTeamFromSeasonButton.js";
import SeasonTables from "../../Components/Season/SeasonTables.js";
import NewMatchDialog from "../../Components/Match/NewMatchDialog.js";
import PageFAB from "../../Components/UI/PageFAB.js";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const TabPanel = ({ value, current, children }) => {
  if (value !== current) return null;
  return <Box mt={2}>{children}</Box>;
};

const SeasonPage = () => {
  const { seasonId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileTab, setMobileTab] = useState("matches");

  const season = useQuery(GET_SEASON, {
    variables: { id: seasonId },
  });

  const matches = useQuery(GET_MATCHES_BY_SEASON_ID, {
    variables: { id: seasonId },
  });

  const teamsInSeason = useQuery(GET_TEAMS_BY_SEASON_ID, {
    variables: { id: seasonId },
  });

  const teamsNotInSeason = useQuery(GET_TEAMS_NOT_BY_SEASON_ID, {
    variables: { id: seasonId },
  });

  let seasonsWithPermission = useQuery(SEASONS_WITH_PERMISSION, {});

  const matchesWithPermission = useQuery(MATCHES_WITH_PERMISSION, {});

  const managers = useQuery(GET_MANAGERS, {});

  const [auth] = useContext(LoginContext);
  const [fabOpen, setFabOpen] = useState(false);

  const queryError =
    season.error ||
    matches.error ||
    teamsInSeason.error ||
    teamsNotInSeason.error ||
    seasonsWithPermission.error ||
    matchesWithPermission.error ||
    managers.error;
  if (queryError) {
    return (
      <PageError
        message={queryError.message}
        onRetry={() => {
          season.refetch();
          matches.refetch();
          teamsInSeason.refetch();
          teamsNotInSeason.refetch();
          seasonsWithPermission.refetch();
          matchesWithPermission.refetch();
          managers.refetch();
        }}
      />
    );
  }

  if (
    season.loading ||
    matches.loading ||
    teamsInSeason.loading ||
    teamsNotInSeason.loading ||
    seasonsWithPermission.loading ||
    matchesWithPermission.loading ||
    managers.loading
  )
    return <LoadingSpinner />;

  seasonsWithPermission = seasonsWithPermission.data.seasonsWithPermission.map(
    (season) => season.id
  );

  const teamsWithPermission = auth.userId
    ? teamsInSeason.data.teamsBySeasonId
        .filter((t) => String(t.manager.user.id) === String(auth.userId))
        .map((t) => ({ id: t.id }))
    : [];

  const canEdit =
    auth.loggedIn && seasonsWithPermission.includes(parseInt(seasonId));

  const teamsContent = (
    <>
      <Typography variant="h5" sx={{ mb: 1 }}>Teams</Typography>
      <TeamTable
        managers={managers.data.managers}
        teams={teamsInSeason.data.teamsBySeasonId}
        teamsWithPermission={teamsWithPermission}
      />
      <Box sx={{ display: "inline-flex" }}>
        {canEdit && (
          <AddTeamToSeasonButton
            sx={{ marginLeft: "auto" }}
            variant="contained"
            seasonId={seasonId}
            teams={teamsNotInSeason.data.teamsNotBySeasonId}
          />
        )}
        {canEdit && (
          <RemoveTeamFromSeasonButton
            sx={{ marginLeft: "auto" }}
            variant="contained"
            seasonId={seasonId}
            teams={teamsInSeason.data.teamsBySeasonId}
          />
        )}
      </Box>
    </>
  );

  const matchesContent = (
    <>
      <Typography variant="h5" sx={{ mb: 1 }}>Matches &amp; Tables</Typography>
      <SeasonTables
        matches={matches.data.matchesBySeasonId}
        matchesWithPermission={
          matchesWithPermission.data.matchesWithPermission
        }
        canEdit={canEdit}
        seasonId={seasonId}
        teams={teamsInSeason.data.teamsBySeasonId}
      />
    </>
  );

  return (
    <PageLayout
      title={season.data.season.name}
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Competitions", to: "/competitions" },
        {
          label: season.data.season.competition.name,
          to: "/competition/" + season.data.season.competition.id,
        },
        { label: season.data.season.name },
      ]}
    >
      <SeasonOverview season={season.data.season} />

      {isMobile ? (
        <Box sx={{ mt: 1 }}>
          <Box
            sx={{
              position: "sticky",
              top: { xs: 56, sm: 64 },
              zIndex: (t) => t.zIndex.appBar - 1,
              backgroundColor: "background.default",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs value={mobileTab} onChange={(_, v) => setMobileTab(v)}>
              <Tab value="teams" label="Teams" />
              <Tab value="matches" label="Matches & Tables" />
            </Tabs>
          </Box>
          <TabPanel value="teams" current={mobileTab}>
            {teamsContent}
          </TabPanel>
          <TabPanel value="matches" current={mobileTab}>
            {matchesContent}
          </TabPanel>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} md={5}>
            {teamsContent}
          </Grid>
          <Grid item xs={12} md={7}>
            {matchesContent}
          </Grid>
        </Grid>
      )}

      {canEdit && (
        <>
          <PageFAB label="Create Match" onClick={() => setFabOpen(true)} />
          <NewMatchDialog
            open={fabOpen}
            setOpen={setFabOpen}
            seasonId={seasonId}
            teams={teamsInSeason.data.teamsBySeasonId}
          />
        </>
      )}
    </PageLayout>
  );
};

export default SeasonPage;
